import { useEffect, useRef, useState } from 'react';
import PunchCoinImage from '../../assets/icons/punch_coin.png';
import HighRewardCrownIcon150 from '../../assets/icons/punch/crown.png';
import HighRewardCrownIcon100 from '../../assets/icons/punch/dia.png';
import HighRewardCrownIcon80 from '../../assets/icons/punch/con.png';
import HighRewardCrownIcon50 from '../../assets/icons/punch/lucky.png';
import { getHighRewardTier, HIGH_REWARD_TIER } from './rewardConfig';

const COIN_SCALE_MULTIPLIER = 1.3;
const COIN_PARTICLE_SIZE_MULTIPLIER = 1.5;
const BURNING_PARTICLE_COUNT_MULTIPLIER = 1.1;
const BURST_CLEANUP_BUFFER_MS = 160;
const RAF_FRAME_INTERVAL_MS = 1000 / 30;
const DEFAULT_PARTICLE_GLOW = 'drop-shadow(0 4px 8px rgba(201,146,22,0.45))';

function getParticleGlowByEffectColor(effectGlowColor) {
  if (!effectGlowColor) return null;
  return `drop-shadow(0 0 8px ${effectGlowColor})`;
}

function getParticleGlowByRewardPoint(rewardPoint) {
  const rewardTier = getHighRewardTier(rewardPoint);
  if (rewardTier === HIGH_REWARD_TIER.BASIC) {
    return 'drop-shadow(0 0 8px rgba(125, 255, 208, 0.55))';
  }
  if (rewardTier === HIGH_REWARD_TIER.NICE) {
    return 'drop-shadow(0 0 8px rgba(255, 216, 137, 0.58))';
  }
  if (rewardTier === HIGH_REWARD_TIER.GREAT) {
    return 'drop-shadow(0 0 8px rgba(201, 160, 255, 0.58))';
  }
  if (rewardTier === HIGH_REWARD_TIER.JACKPOT) {
    return 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.62))';
  }
  return DEFAULT_PARTICLE_GLOW;
}

function getBurstIconByRewardPoint(rewardPoint) {
  const rewardTier = getHighRewardTier(rewardPoint);
  if (rewardTier === HIGH_REWARD_TIER.JACKPOT) return HighRewardCrownIcon150;
  if (rewardTier === HIGH_REWARD_TIER.GREAT) return HighRewardCrownIcon100;
  if (rewardTier === HIGH_REWARD_TIER.NICE) return HighRewardCrownIcon80;
  if (rewardTier === HIGH_REWARD_TIER.BASIC) return HighRewardCrownIcon50;
  return null;
}

function getBurstCount(rewardPoint, multiplier = 1) {
  let baseCount = 12;
  const rewardTier = getHighRewardTier(rewardPoint);
  if (rewardTier === HIGH_REWARD_TIER.JACKPOT) baseCount = 24;
  else if (rewardTier === HIGH_REWARD_TIER.GREAT) baseCount = 20;
  else if (rewardTier === HIGH_REWARD_TIER.NICE) baseCount = 18;
  else if (rewardTier === HIGH_REWARD_TIER.BASIC) baseCount = 16;
  else if (rewardPoint >= 20) baseCount = 14;
  return Math.round(baseCount * multiplier);
}

function createParticles({ rewardPoint, isBurning }) {
  const count = getBurstCount(rewardPoint, isBurning ? BURNING_PARTICLE_COUNT_MULTIPLIER : 1);

  return Array.from({ length: count }).map((_, index) => {
    const side = index % 2 === 0 ? -1 : 1;
    const startX = -6 + Math.random() * 12;
    const startY = -20 + Math.random() * 8;
    const speed = 250 + Math.random() * 220;
    const angle = Math.PI * 0.28 + Math.random() * (Math.PI * 0.28);
    const vx = Math.cos(angle) * speed * side;
    const vy = -(Math.sin(angle) * speed + 70 + Math.random() * 110);
    const gravity = 920 + Math.random() * 360;
    const delay = Math.random() * 90;
    const duration = 760 + Math.random() * 220;
    const rotSpeed = -420 + Math.random() * 840;
    const size = (20 + Math.random() * 12) * COIN_SCALE_MULTIPLIER;

    return {
      id: `${index}-${Math.random().toString(36).slice(2)}`,
      startX,
      startY,
      vx,
      vy,
      gravity,
      delay,
      duration,
      rotSpeed,
      size,
    };
  });
}

export default function PunchRewardBurst({ event, className = '', effectGlowColor = null }) {
  const [bursts, setBursts] = useState([]);
  const burstsRef = useRef([]);
  const timerMapRef = useRef(new Map());
  const particleNodeMapRef = useRef(new Map());
  const rafIdRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  const effectGlowColorRef = useRef(effectGlowColor);

  useEffect(() => {
    effectGlowColorRef.current = effectGlowColor;
  }, [effectGlowColor]);

  useEffect(() => {
    if (!event?.id) return;

    const particles = createParticles({ rewardPoint: event.rewardPoint, isBurning: !!event.isBurning });
    const lifetimeMs = particles.reduce((max, particle) => Math.max(max, particle.delay + particle.duration), 0);

    const iconSrc = getBurstIconByRewardPoint(event.rewardPoint);
    const glow = getParticleGlowByRewardPoint(event.rewardPoint);

    const burst = {
      id: event.id,
      createdAt: performance.now(),
      rewardPoint: event.rewardPoint,
      isBurning: !!event.isBurning,
      particles,
      iconSrc,
      glow,
    };

    setBursts((prev) => {
      const next = [...prev, burst];
      burstsRef.current = next;
      return next;
    });

    const timer = window.setTimeout(() => {
      setBursts((prev) => {
        const next = prev.filter((item) => item.id !== burst.id);
        burstsRef.current = next;
        return next;
      });
      timerMapRef.current.delete(burst.id);
      const deletePrefix = `${burst.id}:`;
      for (const key of particleNodeMapRef.current.keys()) {
        if (key.startsWith(deletePrefix)) {
          particleNodeMapRef.current.delete(key);
        }
      }
    }, lifetimeMs + BURST_CLEANUP_BUFFER_MS);

    timerMapRef.current.set(burst.id, timer);
  }, [event]);

  const hasActiveBursts = bursts.length > 0;
  useEffect(() => {
    if (!hasActiveBursts) {
      if (rafIdRef.current) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      return;
    }

    const tick = (now) => {
      if (now - lastFrameTimeRef.current < RAF_FRAME_INTERVAL_MS) {
        rafIdRef.current = window.requestAnimationFrame(tick);
        return;
      }
      lastFrameTimeRef.current = now;

      const currentGlowColor = effectGlowColorRef.current;

      burstsRef.current.forEach((burst) => {
        const particleGlow = getParticleGlowByEffectColor(currentGlowColor) ?? burst.glow;

        burst.particles.forEach((particle) => {
          const key = `${burst.id}:${particle.id}`;
          const node = particleNodeMapRef.current.get(key);
          if (!node) return;

          const elapsed = now - (burst.createdAt + particle.delay);
          if (elapsed < 0) {
            node.style.opacity = '0';
            node.style.transform = `translate3d(${particle.startX}px, ${particle.startY}px, 0) scale(0.82) rotate(0deg)`;
            return;
          }

          if (elapsed >= particle.duration) {
            if (node.style.opacity !== '0') {
              node.style.opacity = '0';
              node.style.willChange = 'auto';
            }
            return;
          }

          const t = elapsed / 1000;
          const progress = elapsed / particle.duration;
          const x = particle.startX + particle.vx * t;
          const y = particle.startY + particle.vy * t + 0.5 * particle.gravity * t * t;
          const rotation = particle.rotSpeed * t;

          let opacity = 1;
          if (progress < 0.12) {
            opacity = progress / 0.12;
          } else if (progress > 0.74) {
            opacity = Math.max(0, (1 - progress) / 0.26);
          }

          const scale = progress < 0.24 ? 0.82 + (progress / 0.24) * 0.2 : 1.02 - ((progress - 0.24) / 0.76) * 0.1;

          node.style.opacity = `${opacity}`;
          node.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale}) rotate(${rotation}deg)`;
          node.style.filter = currentGlowColor ? 'none' : particleGlow;
        });
      });

      rafIdRef.current = window.requestAnimationFrame(tick);
    };

    rafIdRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (rafIdRef.current) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [hasActiveBursts]);

  useEffect(() => {
    const timerMap = timerMapRef.current;
    const particleNodeMap = particleNodeMapRef.current;
    return () => {
      timerMap.forEach((timerId) => window.clearTimeout(timerId));
      timerMap.clear();
      particleNodeMap.clear();
      if (rafIdRef.current) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  return (
    <div className={`pointer-events-none absolute z-0 ${className}`}>
      {bursts.map((burst) => (
        <div key={burst.id} className="relative h-1 w-1">
          {!burst.isBurning ? (
            <span
              className="punch-reward-spark absolute left-[-10px] top-[-10px] text-[#F5D27A]"
              style={{ fontSize: `${14 * COIN_SCALE_MULTIPLIER}px` }}
            >
              ✦
            </span>
          ) : null}
          {burst.particles.map((particle) => {
            const particleKey = `${burst.id}:${particle.id}`;
            return (
              <span
                key={particle.id}
                ref={(node) => {
                  if (node) {
                    particleNodeMapRef.current.set(particleKey, node);
                  } else {
                    particleNodeMapRef.current.delete(particleKey);
                  }
                }}
                className="absolute left-0 top-0 leading-none"
                style={{
                  opacity: 0,
                  transform: `translate3d(${particle.startX}px, ${particle.startY}px, 0) scale(0.82)`,
                  willChange: 'transform, opacity',
                  fontSize: `${particle.size}px`,
                }}
              >
                {burst.iconSrc ? (
                  <img
                    src={burst.iconSrc}
                    alt=""
                    aria-hidden="true"
                    style={{
                      width: `${particle.size}px`,
                      height: `${particle.size}px`,
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <img
                    src={PunchCoinImage}
                    alt=""
                    style={{
                      width: `${particle.size * COIN_PARTICLE_SIZE_MULTIPLIER}px`,
                      height: `${particle.size * COIN_PARTICLE_SIZE_MULTIPLIER}px`,
                    }}
                  />
                )}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}
